import { tagged } from "@mirage/service-logging";
import { of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { createConversationResponseObservable } from "./context_observables";

import type {
  AgentId,
  ConversationClientContext,
  DocumentInfo,
  ProgressUpdate,
} from "@mirage/service-conversation/types";
// eslint-disable-next-line boundaries/element-types
import { transformDocument } from "@mirage/service-conversation/utils/convertConversationMessages";

import type { Observable } from "rxjs";
import type { APIv2GRPCWebError } from "../api_v2";
import type { ConversationsAssistantResponseWithJsonMessages } from "./context_observables";
import type { SearchParams } from "./gen/context_engine_conversations_service_pb";
import type { ConversationsChatMessage } from "./gen/conversations_pb";
import type { ConversationsResult, ConversationsResultError } from "./types";

const logger = tagged("conversations_grpc");

export function getConversationResponse(
  userMessage: string,
  serverConversationId: string | undefined,
  history?: ConversationsChatMessage[],
  experimentSetting?: string,
  source?: string,
  modelChoice?: string,
  agentPayload?: string,
  attachments?: DocumentInfo[],
  agentIds?: AgentId[],
  rephrased?: string,
  clientContext?: ConversationClientContext,
  searchParams?: SearchParams,
  skipPersistence?: boolean,
  fssSearchScope?: string,
  llmIgnoreUserMessage?: boolean,
  stackId?: string,
): Observable<ConversationsResult | ConversationsResultError> {
  const conversation$ = createConversationResponseObservable(
    userMessage,
    serverConversationId,
    history,
    experimentSetting,
    source,
    modelChoice,
    agentPayload,
    attachments,
    agentIds,
    rephrased,
    clientContext,
    searchParams,
    skipPersistence,
    fssSearchScope,
    llmIgnoreUserMessage,
    stackId,
  );

  return conversation$.pipe(
    map(
      (
        response: ConversationsAssistantResponseWithJsonMessages,
      ): ConversationsResult => {
        return {
          serverConversationId: response.conversationId,
          messages: response.messages ?? [],
          timestamp: new Date().toISOString(),
          suggestedQueries: response.suggestedQueries ?? [],
          progressUpdates:
            response.progressUpdates?.map((update) => ({
              step: update.step,
              context: update.context.map(transformDocument),
              agentId: update.agentId,
            })) ?? ([] as ProgressUpdate[]),
          unprocessedResponse: response.unprocessedResponse,
          assistRequestId: response.requestId,
          spanId: response.spanId,
        };
      },
    ),
    catchError((error) => {
      logger.error("getConversationResponse error", error);
      const errorSummary: ConversationsResultError = {
        error: "Failed to fetch conversation response. " + error,
        timestamp: new Date().toISOString(),
        requestId: (error as APIv2GRPCWebError).dropboxRequestId,
      };
      return of(errorSummary);
    }),
  );
}
